import { useEffect, useRef } from "react";
import { UnboxMark } from "./UnboxMark";

export type HandoffPhase = "show" | "fly";

const HOLD_MS = 1250;
const FLY_MS = 950;
const EASE = "cubic-bezier(0.65, 0, 0.35, 1)";

/**
 * Shows the question large in the middle of the drawer, then flies it into the first
 * message bubble of the thread. It measures the thread's `.ask-glass-bubble`, so it
 * must sit beside the thread inside the same `.ask-new` container. With reduced
 * motion, or when the bubble is not there, it ends straight away.
 */
export function AskHandoff({
  question,
  onPhase,
  onDone,
}: {
  question: string;
  onPhase: (phase: HandoffPhase) => void;
  onDone: () => void;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const markRef = useRef<HTMLSpanElement>(null);
  const pillRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let flight: Animation | undefined;
    const timer = setTimeout(
      () => {
        const pill = pillRef.current;
        const root = rootRef.current;
        const bubble = root?.parentElement?.querySelector<HTMLElement>(".ask-glass-bubble");
        if (reduced || !pill || !root || !bubble || !pill.parentElement) {
          onDone();
          return;
        }
        onPhase("fly");
        const box = root.getBoundingClientRect();
        const from = pill.getBoundingClientRect();
        const to = bubble.getBoundingClientRect();
        pill.parentElement.style.height = `${pill.parentElement.getBoundingClientRect().height}px`;
        Object.assign(pill.style, {
          position: "absolute",
          margin: "0",
          animation: "none",
          top: `${from.top - box.top}px`,
          right: `${box.right - from.right}px`,
          maxWidth: `${from.width}px`,
        });
        markRef.current?.animate(
          [
            { opacity: 1, transform: "none" },
            { opacity: 0, transform: "translateY(-16px) scale(0.8)" },
          ],
          { duration: 420, easing: "ease-out", fill: "forwards" },
        );
        flight = pill.animate(
          [
            {
              top: `${from.top - box.top}px`,
              right: `${box.right - from.right}px`,
              maxWidth: `${from.width}px`,
              fontSize: "34px",
              fontWeight: "300",
              lineHeight: "39px",
              letterSpacing: "-0.68px",
              padding: "23px 36px",
              borderWidth: "2px",
              borderRadius: "45px 45px 14px 45px",
              backgroundColor: "rgb(192 167 242 / 0)",
              borderColor: "rgb(255 255 255 / 0)",
            },
            { backgroundColor: "rgb(192 167 242 / 0.16)", borderColor: "rgb(255 255 255 / 0.14)", offset: 0.55 },
            {
              top: `${to.top - box.top}px`,
              right: `${box.right - to.right}px`,
              maxWidth: `${to.width}px`,
              fontSize: "15px",
              fontWeight: "400",
              lineHeight: "22px",
              letterSpacing: "0px",
              padding: "10px 16px",
              borderWidth: "1px",
              borderRadius: "20px 20px 6px 20px",
              backgroundColor: "rgb(192 167 242 / 0.16)",
              borderColor: "rgb(255 255 255 / 0.14)",
            },
          ],
          { duration: FLY_MS, easing: EASE, fill: "forwards" },
        );
        pill.firstElementChild?.animate([{ color: "rgb(255 255 255 / 0)" }, { color: "rgb(255 255 255)" }], {
          duration: FLY_MS,
          easing: EASE,
          fill: "forwards",
        });
        flight.onfinish = onDone;
      },
      reduced ? 0 : HOLD_MS,
    );
    return () => {
      clearTimeout(timer);
      flight?.cancel();
    };
  }, [onPhase, onDone]);

  return (
    <div ref={rootRef} className="ask-handoff" aria-hidden="true">
      <span ref={markRef} className="ask-handoff-mark">
        <UnboxMark size={30} motion="intro" className="text-[#d6bbfb]" />
      </span>
      <h2 className="ask-greeting ask-handoff-question">
        <span ref={pillRef} className="ask-handoff-pill">
          <span className="ask-handoff-text">{question}</span>
        </span>
      </h2>
    </div>
  );
}
